import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { 
  Zap, Loader2, Image as ImageIcon, Film, Users, Play, Pause,
  Upload, Trash2, Plus, ChevronDown, ChevronRight, ChevronLeft,
  Sparkles, Check, X, RefreshCw, AlertTriangle, Settings2, Eye,
  FileVideo, StickyNote, Pencil, FolderOpen, Star, Maximize2, Minimize2,
  Download, Sliders
} from "lucide-react";
import { getAssetUrl, API } from "@/utils/studioUtils";
import api from "@/config/axiosInstance";
import { toast } from "sonner";
import { SegmentEditor } from "./SegmentEditor";

/**
 * ExtremeActionPanel - Panel for Extreme Action mode in Preview step
 * Handles the slow-motion generation pipeline for fight scenes
 */
export function ExtremeActionPanel({
  scene,
  sceneId,
  projectId,
  actionDirection,
  o1Elements,
  assets = [],
  characterProfiles = [],
  onGenerationComplete
}) {
  // Pipeline state
  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [jobId, setJobId] = useState(null);
  
  // Analysis results
  const [analysisResult, setAnalysisResult] = useState(null);
  const [durationWarning, setDurationWarning] = useState(false);
  
  // Settings (speed only in micro-editor after first take)
  const [speedMultiplier, setSpeedMultiplier] = useState(5);
  
  // Reference images per element
  const [elementRefs, setElementRefs] = useState({});
  
  // Additional reference images (not attached to characters)
  const [additionalRefs, setAdditionalRefs] = useState([]);
  
  // UI state
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showMicroEdit, setShowMicroEdit] = useState(false);
  const [existingState, setExistingState] = useState(null);
  const [expandedElement, setExpandedElement] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  
  // Phased workflow states
  const [animating, setAnimating] = useState(false);  // Video generation in progress
  const [compiling, setCompiling] = useState(false);  // Final compilation in progress
  
  // Show segment editor (edit step before compile)
  const [showSegmentEditor, setShowSegmentEditor] = useState(false);
  
  // Micro-edit accordion expanded state
  const [microEditExpanded, setMicroEditExpanded] = useState({
    poses: true,  // Pose images - starts expanded
    animationSetup: false,  // Animation Setup - starts collapsed, expands when ready
    segments: false,  // Video segments - legacy, may still be used
    compile: true
  });
  
  // Micro-edit action states
  const [regeneratingPose, setRegeneratingPose] = useState(null);
  const [regeneratingSegment, setRegeneratingSegment] = useState(null);
  const [recompiling, setRecompiling] = useState(false);
  
  // Fullscreen micro-editor modal
  const [showFullscreenEditor, setShowFullscreenEditor] = useState(false);
  
  // Editing prompt state
  const [editingPrompt, setEditingPrompt] = useState(null);
  
  // Asset library modal state
  const [showAssetLibrary, setShowAssetLibrary] = useState({ open: false, elementId: null, elementName: "" });
  const [libraryViewMode, setLibraryViewMode] = useState('character'); // 'character' or 'all'
  
  // Additional refs modal
  const [showAdditionalRefsModal, setShowAdditionalRefsModal] = useState(false);
  
  // Confirmation dialog
  const [showDurationConfirm, setShowDurationConfirm] = useState(false);
  
  // Video playback modal
  const [playingVideo, setPlayingVideo] = useState(null);
  
  // Fullscreen image view
  const [fullscreenImage, setFullscreenImage] = useState(null);
  
  // Edited prompts for poses and segments (local state before regeneration)
  const [editedPosePrompts, setEditedPosePrompts] = useState({});
  const [editedSegmentPrompts, setEditedSegmentPrompts] = useState({});
  
  // Track which poses/segments have been regenerated (for enabling action buttons)
  const [posesRegenerated, setPosesRegenerated] = useState(false);
  const [segmentsRegenerated, setSegmentsRegenerated] = useState(false);
  
  // Carousel indices for versions
  const [poseVersionIndices, setPoseVersionIndices] = useState({});
  const [segmentVersionIndices, setSegmentVersionIndices] = useState({});
  
  // Generating new segments state
  const [generatingNewSegments, setGeneratingNewSegments] = useState(false);
  
  // Animation Setup state
  const [settingUpAnimation, setSettingUpAnimation] = useState(false);
  const [animationSetupReady, setAnimationSetupReady] = useState(false);
  const [generatingSegments, setGeneratingSegments] = useState(new Set()); // Set of segment indices being generated
  const [segmentProgress, setSegmentProgress] = useState({}); // Progress per segment { [index]: percent }
  
  // Polling interval refs for each segment
  const segmentPollRefs = useRef({});
  const segmentProgressRefs = useRef({});
  
  // Polling interval ref
  const pollRef = useRef(null);

  // All assets for reference picker (characters, backgrounds, items, sets)
  const allAssets = assets || [];
  const characterAssets = allAssets.filter(a => a.type === 'character');
  const backgroundAssets = allAssets.filter(a => a.type === 'background');

  // Get character profile by element
  const getCharacterProfile = (element) => {
    if (!element) return null;
    return characterProfiles.find(p => 
      p.id === element.character_profile_id || 
      p.name?.toLowerCase() === element.name?.toLowerCase()
    );
  };

  // Track if saved refs have been loaded
  const [refsLoaded, setRefsLoaded] = useState(false);

  // Load saved references on mount
  useEffect(() => {
    loadSavedReferences();
  }, [sceneId]);

  // Initialize element refs from o1Elements ONLY after saved refs are loaded and if no saved refs exist
  useEffect(() => {
    if (refsLoaded && o1Elements?.length > 0 && Object.keys(elementRefs).length === 0) {
      const initialRefs = {};
      o1Elements.forEach(elem => {
        const elemId = elem.character_profile_id || elem.name;
        initialRefs[elemId] = {
          element_id: elemId,
          element_name: elem.name,
          frontal_image: elem.frontal_image_url,
          images: (elem.reference_image_urls || []).map(url => ({ url, note: "" }))
        };
      });
      setElementRefs(initialRefs);
    }
  }, [o1Elements, refsLoaded]);

  // Load existing state on mount
  useEffect(() => {
    loadExistingState();
  }, [sceneId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
    };
  }, []);

  // Save references when they change
  useEffect(() => {
    if (Object.keys(elementRefs).length > 0 || additionalRefs.length > 0) {
      saveReferences();
    }
  }, [elementRefs, additionalRefs]);

  const loadSavedReferences = async () => {
    try {
      const response = await api.get(`/extreme-action/scenes/${sceneId}/saved-references`);
      if (response.data.element_refs && Object.keys(response.data.element_refs).length > 0) {
        setElementRefs(response.data.element_refs);
      }
      if (response.data.additional_refs && response.data.additional_refs.length > 0) {
        setAdditionalRefs(response.data.additional_refs);
      }
    } catch (error) {
      console.error("Error loading saved references:", error);
    } finally {
      setRefsLoaded(true);
    }
  };

  const saveReferences = async () => {
    try {
      await api.post(`/extreme-action/scenes/${sceneId}/save-references`, {
        element_refs: elementRefs,
        additional_refs: additionalRefs
      });
    } catch (error) {
      console.error("Error saving references:", error);
    }
  };

  const loadExistingState = async () => {
    try {
      const response = await api.get(`/extreme-action/scenes/${sceneId}/state`);
      if (response.data.exists) {
        setExistingState(response.data.state);
        if (response.data.state.speed_multiplier) {
          setSpeedMultiplier(response.data.state.speed_multiplier);
        }
        // Show micro-edit if there's any progress (poses, prompts, or completed/paused status)
        const state = response.data.state;
        if (state.status === "complete" || 
            state.status === "paused" || 
            state.status === "error" ||
            state.status === "animation_setup" ||
            state.pose_images?.length > 0 ||
            state.transition_prompts?.length > 0) {
          setShowMicroEdit(true);
        }
      }
    } catch (error) {
      console.error("Error loading extreme action state:", error);
    }
  };

  const handleAnalyze = async () => {
    if (!actionDirection?.trim()) {
      toast.error("Please enter an action direction in the Compose step first");
      return;
    }

    setAnalyzing(true);
    setAnalysisResult(null);

    try {
      const response = await api.post(`/extreme-action/scenes/${sceneId}/analyze`, {
        action_direction: actionDirection,
        composite_image_url: scene?.composite_image_url
      });

      if (response.data.success) {
        setAnalysisResult(response.data);
        setShowAnalysis(true);
        
        if (response.data.duration_warning) {
          setDurationWarning(true);
        }
        
        toast.success(`Analyzed! ${response.data.num_poses} poses, ~${response.data.estimated_cost_sparks} sparks`);
      }
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error(error.response?.data?.detail || "Failed to analyze action");
    } finally {
      setAnalyzing(false);
    }
  };

  const handleStartPipeline = async (acknowledgeWarning = false) => {
    if (!analysisResult) {
      await handleAnalyze();
      return;
    }

    // Check for duration warning
    if (analysisResult.duration_warning && !acknowledgeWarning) {
      setShowDurationConfirm(true);
      return;
    }

    setGenerating(true);
    setShowDurationConfirm(false);

    try {
      // Build reference images from elementRefs
      const refs = Object.values(elementRefs).map(ref => ({
        element_id: ref.element_id,
        element_name: ref.element_name,
        image_urls: [ref.frontal_image, ...ref.images.map(i => i.url)].filter(Boolean),
        image_notes: ref.images.map(i => i.note || "")
      }));

      // Get background image from scene
      const backgroundUrl = scene?.background_url || scene?.background_image_url;

      const response = await api.post(`/extreme-action/scenes/${sceneId}/start-pipeline`, {
        action_direction: actionDirection,
        composite_image_url: scene?.composite_image_url,
        background_image_url: backgroundUrl,
        reference_images: refs,
        additional_references: additionalRefs,
        speed_multiplier: speedMultiplier,
        duration_warning_acknowledged: acknowledgeWarning
      });

      if (response.data.success) {
        setJobId(response.data.job_id);
        toast.success("Generating pose images...");
        startPolling(response.data.job_id);
      } else if (response.data.requires_confirmation) {
        setShowDurationConfirm(true);
      }
    } catch (error) {
      console.error("Pipeline error:", error);
      if (error.response?.status === 402) {
        toast.error(`Insufficient sparks. Need ${error.response.data.detail.sparks_needed}, have ${error.response.data.detail.current_balance}`);
      } else {
        toast.error(error.response?.data?.detail || "Failed to start pipeline");
      }
      setGenerating(false);
    }
  };

  const startPolling = (id) => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
    }

    pollRef.current = setInterval(async () => {
      try {
        const response = await api.get(`/extreme-action/scenes/${sceneId}/status/${id}`);
        setPipelineStatus(response.data);

        // Phase 1 complete - poses ready
        if (response.data.status === "poses_complete") {
          clearInterval(pollRef.current);
          setGenerating(false);
          setShowMicroEdit(true);
          setShowFullscreenEditor(true); // Auto-open modal
          await loadExistingState();
          toast.success("Pose images ready! Review and click Animate to continue.");
        }
        // Phase 2 complete - videos ready
        else if (response.data.status === "segments_complete" || response.data.status === "segments_partial") {
          clearInterval(pollRef.current);
          setGenerating(false);
          setAnimating(false);
          await loadExistingState();
          // Auto-expand video section
          setMicroEditExpanded(prev => ({ ...prev, segments: true }));
          // Scroll to video section
          setTimeout(() => {
            document.getElementById('video-segments-section')?.scrollIntoView({ behavior: 'smooth' });
          }, 300);
          if (response.data.status === "segments_partial") {
            toast.warning("Some video segments failed - you can retry them.");
          } else {
            toast.success("Video segments ready! Review and click Compile to finish.");
          }
        }
        // Phase 3 complete - final video ready
        else if (response.data.status === "complete") {
          clearInterval(pollRef.current);
          setGenerating(false);
          setCompiling(false);
          await loadExistingState();
          // Scroll to final video section
          setTimeout(() => {
            document.getElementById('final-video-section')?.scrollIntoView({ behavior: 'smooth' });
          }, 300);
          toast.success("Extreme action video complete!");
        } 
        else if (response.data.status === "error") {
          clearInterval(pollRef.current);
          setGenerating(false);
          setAnimating(false);
          setCompiling(false);
          toast.error(response.data.progress_message || "Pipeline failed");
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    }, 3000);
  };

  // Start video generation (Phase 2) - called when user clicks "Animate"
  const handleAnimate = async () => {
    try {
      setAnimating(true);
      const currentJobId = existingState?.id || jobId;
      
      const response = await api.post(`/extreme-action/scenes/${sceneId}/animate/${currentJobId}`);
      
      if (response.data.success) {
        toast.success("Generating video segments...");
        startPolling(currentJobId);
      }
    } catch (error) {
      console.error("Animate error:", error);
      if (error.response?.status === 402) {
        toast.error("Insufficient sparks for video generation");
      } else {
        toast.error(error.response?.data?.detail || "Failed to start video generation");
      }
      setAnimating(false);
    }
  };

  // Set up animation - generates prompts only, no videos
  const handleSetupAnimation = async () => {
    try {
      setSettingUpAnimation(true);
      const currentJobId = existingState?.id || jobId;
      
      const response = await api.post(`/extreme-action/scenes/${sceneId}/setup-animation/${currentJobId}`);
      
      if (response.data.success) {
        toast.success("Animation setup ready! Review and animate each segment.");
        setAnimationSetupReady(true);
        // Reload state to get the new prompts and segments
        await loadExistingState();
        // Expand the animation setup section
        setMicroEditExpanded(prev => ({ ...prev, animationSetup: true }));
      }
    } catch (error) {
      console.error("Setup animation error:", error);
      toast.error(error.response?.data?.detail || "Failed to set up animation");
    } finally {
      setSettingUpAnimation(false);
    }
  };

  // Animate a single segment (supports parallel generation)
  const handleAnimateSegment = async (segmentIndex) => {
    // Don't start if already generating this segment
    if (generatingSegments.has(segmentIndex)) return;
    
    try {
      // Add to generating set
      setGeneratingSegments(prev => new Set([...prev, segmentIndex]));
      // Initialize progress for this segment
      setSegmentProgress(prev => ({ ...prev, [segmentIndex]: 0 }));
      
      const currentJobId = existingState?.id || jobId;
      
      // Save the current prompt first if it was edited
      const currentPrompt = editedSegmentPrompts[segmentIndex];
      if (currentPrompt !== undefined) {
        await api.post(`/extreme-action/scenes/${sceneId}/update-segment-prompt/${currentJobId}/${segmentIndex}`, {
          prompt: currentPrompt
        });
      }
      
      const response = await api.post(`/extreme-action/scenes/${sceneId}/animate-segment/${currentJobId}/${segmentIndex}`);
      
      if (response.data.success) {
        toast.success(`Generating video for segment ${segmentIndex + 1}...`);
        // Start progress animation and polling
        startSegmentProgressAnimation(segmentIndex);
        pollForSegmentCompletion(currentJobId, segmentIndex);
      }
    } catch (error) {
      console.error("Animate segment error:", error);
      if (error.response?.status === 402) {
        toast.error("Insufficient sparks for video generation");
      } else {
        toast.error(error.response?.data?.detail || "Failed to start video generation");
      }
      // Remove from generating set
      setGeneratingSegments(prev => {
        const newSet = new Set(prev);
        newSet.delete(segmentIndex);
        return newSet;
      });
      setSegmentProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[segmentIndex];
        return newProgress;
      });
    }
  };

  // Animated progress bar - goes to 80% at estimated rate, then slows for last 20%
  const startSegmentProgressAnimation = (segmentIndex) => {
    const estimatedDuration = 90000; // 90 seconds estimated
    const fastPhase = 0.8; // 80%
    const fastDuration = estimatedDuration * 0.6; // 60% of time for first 80%
    const startTime = Date.now();
    
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      let progress;
      
      if (elapsed < fastDuration) {
        // Fast phase: 0-80% in first 60% of estimated time
        progress = (elapsed / fastDuration) * fastPhase * 100;
      } else {
        // Slow phase: 80-95% in remaining time (never hits 100 until complete)
        const slowElapsed = elapsed - fastDuration;
        const slowProgress = Math.min(0.15, (slowElapsed / (estimatedDuration * 0.4)) * 0.15);
        progress = (fastPhase + slowProgress) * 100;
      }
      
      setSegmentProgress(prev => ({ ...prev, [segmentIndex]: Math.min(progress, 95) }));
    }, 100);
    
    // Store the interval ref for cleanup
    segmentProgressRefs.current[segmentIndex] = progressInterval;
  };

  // Poll for single segment completion (supports parallel)
  const pollForSegmentCompletion = (currentJobId, segmentIndex) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/extreme-action/scenes/${sceneId}/state`);
        if (response.data.exists && response.data.state) {
          const segments = response.data.state.video_segments || [];
          const segment = segments[segmentIndex];
          
          if (segment?.status === 'complete' || segment?.status === 'error') {
            // Clear polling and progress intervals
            clearInterval(pollInterval);
            if (segmentProgressRefs.current[segmentIndex]) {
              clearInterval(segmentProgressRefs.current[segmentIndex]);
              delete segmentProgressRefs.current[segmentIndex];
            }
            
            // Set progress to 100% briefly before removing
            setSegmentProgress(prev => ({ ...prev, [segmentIndex]: 100 }));
            
            // Remove from generating set after a short delay
            setTimeout(() => {
              setGeneratingSegments(prev => {
                const newSet = new Set(prev);
                newSet.delete(segmentIndex);
                return newSet;
              });
              setSegmentProgress(prev => {
                const newProgress = { ...prev };
                delete newProgress[segmentIndex];
                return newProgress;
              });
            }, 500);
            
            // Reload state
            await loadExistingState();
            
            // Set version index to latest
            if (segment.status === 'complete') {
              const versionsCount = segment.versions?.length || 0;
              if (versionsCount > 0) {
                setSegmentVersionIndices(prev => ({ ...prev, [segmentIndex]: versionsCount - 1 }));
              }
              toast.success(`Segment ${segmentIndex + 1} generated!`);
            } else {
              toast.error(`Segment ${segmentIndex + 1} failed: ${segment.error_message || 'Unknown error'}`);
            }
          }
        }
      } catch (error) {
        console.error("Segment polling error:", error);
      }
    }, 2000);
    
    // Store poll interval ref for cleanup
    segmentPollRefs.current[segmentIndex] = pollInterval;
    
    // Timeout after 5 minutes
    setTimeout(() => {
      if (segmentPollRefs.current[segmentIndex]) {
        clearInterval(segmentPollRefs.current[segmentIndex]);
        delete segmentPollRefs.current[segmentIndex];
      }
      if (segmentProgressRefs.current[segmentIndex]) {
        clearInterval(segmentProgressRefs.current[segmentIndex]);
        delete segmentProgressRefs.current[segmentIndex];
      }
      
      setGeneratingSegments(prev => {
        if (prev.has(segmentIndex)) {
          const newSet = new Set(prev);
          newSet.delete(segmentIndex);
          toast.error(`Segment ${segmentIndex + 1} timed out. Please try again.`);
          return newSet;
        }
        return prev;
      });
      setSegmentProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[segmentIndex];
        return newProgress;
      });
    }, 300000);
  };

  // Update segment prompt
  const handleUpdateSegmentPrompt = async (segmentIndex, newPrompt) => {
    const currentJobId = existingState?.id || jobId;
    try {
      await api.post(`/extreme-action/scenes/${sceneId}/update-segment-prompt/${currentJobId}/${segmentIndex}`, {
        prompt: newPrompt
      });
      // Update local state
      setEditedSegmentPrompts(prev => ({ ...prev, [segmentIndex]: newPrompt }));
    } catch (error) {
      console.error("Update prompt error:", error);
    }
  };

  // Compile final video (Phase 3) - called when user clicks "Compile"
  const handleCompile = async () => {
    try {
      setCompiling(true);
      const currentJobId = existingState?.id || jobId;
      
      const response = await api.post(
        `/extreme-action/scenes/${sceneId}/compile/${currentJobId}?speed_multiplier=${speedMultiplier}`
      );
      
      if (response.data.success) {
        toast.success(`Compiling at ${speedMultiplier}x speed...`);
        startPolling(currentJobId);
      }
    } catch (error) {
      console.error("Compile error:", error);
      toast.error(error.response?.data?.detail || "Failed to start compilation");
      setCompiling(false);
    }
  };

  // Compile with edits from Segment Editor (Phase 3 with user edits)
  const handleCompileWithEdits = async (editedSegments) => {
    try {
      setCompiling(true);
      setShowSegmentEditor(false);
      const currentJobId = existingState?.id || jobId;
      
      // Transform segments for the API
      const segmentEdits = editedSegments.map(seg => ({
        id: seg.id,
        order: seg.order,
        trim_start_ms: seg.trim_start_ms || 0,
        trim_end_ms: seg.trim_end_ms || 0,
        speed: seg.speed || 1.0
      }));
      
      const response = await api.post(
        `/extreme-action/scenes/${sceneId}/compile-with-edits/${currentJobId}`,
        {
          segments: segmentEdits,
          global_speed: speedMultiplier
        }
      );
      
      if (response.data.success) {
        toast.success(`Compiling with edits at ${speedMultiplier}x speed...`);
        startPolling(currentJobId);
      }
    } catch (error) {
      console.error("Compile with edits error:", error);
      toast.error(error.response?.data?.detail || "Failed to start compilation");
      setCompiling(false);
    }
  };

  // Open Segment Editor
  const handleOpenSegmentEditor = () => {
    setShowSegmentEditor(true);
  };

  // Navigate to Animation Review
  const handleReviewAnimation = () => {
    if (onGenerationComplete && existingState?.final_video_url) {
      onGenerationComplete(existingState.final_video_url);
    }
  };

  const handleRetrySegment = async (segmentId) => {
    try {
      setRegeneratingSegment(segmentId);
      await api.post(`/extreme-action/scenes/${sceneId}/retry-segment/${segmentId}`);
      toast.info("Retrying segment...");
      setGenerating(true);
      startPolling(existingState?.id || jobId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to retry segment");
    } finally {
      setRegeneratingSegment(null);
    }
  };

  // Regenerate a single pose image with custom prompt
  const handleRegeneratePose = async (poseIndex, customPrompt = null) => {
    try {
      setRegeneratingPose(poseIndex);
      // Use edited prompt if available, otherwise use the breakdown description
      const prompt = customPrompt || editedPosePrompts[poseIndex] || existingState?.action_breakdown?.[poseIndex]?.description;
      
      const response = await api.post(`/extreme-action/scenes/${sceneId}/regenerate-pose/${poseIndex}`, {
        job_id: existingState?.id,
        custom_prompt: prompt
      });
      
      if (response.data.success) {
        toast.success("Pose regenerated!");
        setPosesRegenerated(true);
        // Reload state to get updated pose with versions
        await loadExistingState();
        // Set version index to the LATEST (most recent) version for this pose
        // The new version is appended to the end of the versions array
        const updatedState = await api.get(`/extreme-action/scenes/${sceneId}/state`);
        if (updatedState.data.exists && updatedState.data.state) {
          const pose = updatedState.data.state.pose_images?.[poseIndex];
          const versionsCount = pose?.versions?.length || 0;
          if (versionsCount > 0) {
            setPoseVersionIndices(prev => ({ ...prev, [poseIndex]: versionsCount - 1 }));
          }
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to regenerate pose");
    } finally {
      setRegeneratingPose(null);
    }
  };

  // Regenerate all poses
  const handleRegenerateAllPoses = async () => {
    try {
      setRegeneratingPose(-1); // -1 indicates "all"
      const response = await api.post(`/extreme-action/scenes/${sceneId}/regenerate-all-poses`, {
        job_id: existingState?.id
      });
      
      if (response.data.success) {
        toast.success("All poses regenerated!");
        setPosesRegenerated(true);
        await loadExistingState();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to regenerate poses");
    } finally {
      setRegeneratingPose(null);
    }
  };

  // Regenerate a single video segment with custom prompt
  const handleRegenerateSegment = async (segmentIndex, customPrompt = null) => {
    try {
      setRegeneratingSegment(segmentIndex);
      const prompt = customPrompt || editedSegmentPrompts[segmentIndex] || existingState?.transition_prompts?.[segmentIndex]?.prompt;
      
      const response = await api.post(`/extreme-action/scenes/${sceneId}/regenerate-segment/${segmentIndex}`, {
        job_id: existingState?.id,
        custom_prompt: prompt
      });
      
      if (response.data.success) {
        toast.success("Segment regenerated!");
        setSegmentsRegenerated(true);
        await loadExistingState();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to regenerate segment");
    } finally {
      setRegeneratingSegment(null);
    }
  };

  // Generate new video segments from regenerated poses
  const handleGenerateNewSegments = async () => {
    try {
      setGeneratingNewSegments(true);
      const response = await api.post(`/extreme-action/scenes/${sceneId}/generate-segments`, {
        job_id: existingState?.id
      });
      
      if (response.data.success) {
        toast.success("Generating new video segments...");
        setPosesRegenerated(false);
        // Start polling for completion
        startPolling(existingState?.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start segment generation");
      setGeneratingNewSegments(false);
    }
  };

  // Compile video segments into final shot
  const handleCompileShot = async () => {
    try {
      setRecompiling(true);
      const response = await api.post(`/extreme-action/scenes/${sceneId}/recompile`, {
        job_id: existingState?.id,
        speed_multiplier: speedMultiplier
      });
      
      if (response.data.success) {
        toast.success(`Shot compiled at ${speedMultiplier}X speed!`);
        setSegmentsRegenerated(false);
        setExistingState(prev => ({
          ...prev,
          final_video_url: response.data.final_video_url,
          speed_multiplier: speedMultiplier
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to compile shot");
    } finally {
      setRecompiling(false);
    }
  };

  // Recompile final video with new speed
  const handleRecompile = async () => {
    try {
      setRecompiling(true);
      const response = await api.post(`/extreme-action/scenes/${sceneId}/recompile`, {
        job_id: existingState?.id,
        speed_multiplier: speedMultiplier
      });
      
      if (response.data.success) {
        toast.success(`Video recompiled at ${speedMultiplier}X!`);
        setExistingState(prev => ({
          ...prev,
          final_video_url: response.data.final_video_url,
          speed_multiplier: speedMultiplier
        }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to recompile video");
    } finally {
      setRecompiling(false);
    }
  };

  // Download a video segment
  const handleDownloadSegment = async (videoUrl, segmentIndex) => {
    try {
      const response = await fetch(getAssetUrl(videoUrl));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `segment_${segmentIndex + 1}.mp4`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error("Failed to download segment");
    }
  };

  // Navigate pose version carousel
  const handlePoseVersionChange = (poseIndex, direction) => {
    const pose = existingState?.pose_images?.[poseIndex];
    const versions = pose?.versions || [];
    if (versions.length <= 1) return;
    
    const currentIdx = poseVersionIndices[poseIndex] || 0;
    const newIdx = direction === 'next' 
      ? (currentIdx + 1) % versions.length
      : (currentIdx - 1 + versions.length) % versions.length;
    
    setPoseVersionIndices(prev => ({ ...prev, [poseIndex]: newIdx }));
  };

  // Navigate segment version carousel
  const handleSegmentVersionChange = (segmentIndex, direction) => {
    const segment = existingState?.video_segments?.[segmentIndex];
    const versions = segment?.versions || [];
    if (versions.length <= 1) return;
    
    const currentIdx = segmentVersionIndices[segmentIndex] || 0;
    const newIdx = direction === 'next' 
      ? (currentIdx + 1) % versions.length
      : (currentIdx - 1 + versions.length) % versions.length;
    
    setSegmentVersionIndices(prev => ({ ...prev, [segmentIndex]: newIdx }));
  };

  // Get current pose image URL (from version carousel or main)
  const getPoseImageUrl = (pose, poseIndex) => {
    const versions = pose?.versions || [];
    const versionIdx = poseVersionIndices[poseIndex] || 0;
    if (versions.length > 0 && versions[versionIdx]) {
      return versions[versionIdx].image_url;
    }
    return pose?.image_url;
  };

  // Get current segment video URL (from version carousel or main)
  const getSegmentVideoUrl = (segment, segmentIndex) => {
    const versions = segment?.versions || [];
    const versionIdx = segmentVersionIndices[segmentIndex] || 0;
    if (versions.length > 0 && versions[versionIdx]) {
      return versions[versionIdx].video_url;
    }
    return segment?.video_url;
  };

  // Download a pose image
  const handleDownloadPoseImage = async (imageUrl, poseIndex) => {
    try {
      const response = await fetch(getAssetUrl(imageUrl));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pose_${poseIndex + 1}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error("Failed to download image");
    }
  };

  // Upload a custom pose image
  const handleUploadPoseImage = async (file, poseIndex) => {
    if (!file || !existingState?.id) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('job_id', existingState.id);
      formData.append('pose_index', poseIndex.toString());
      
      const response = await api.post(
        `/extreme-action/scenes/${sceneId}/upload-pose-image/${poseIndex}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        toast.success("Pose image uploaded!");
        setPosesRegenerated(true);
        await loadExistingState();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
    }
  };

  // Update a prompt (breakdown or transition)
  const handleUpdatePrompt = async (promptType, index, newPrompt) => {
    try {
      await api.post(`/extreme-action/scenes/${sceneId}/update-prompt`, {
        job_id: existingState?.id,
        prompt_type: promptType,
        index,
        new_prompt: newPrompt
      });
      
      // Update local state
      if (promptType === "breakdown") {
        setExistingState(prev => ({
          ...prev,
          action_breakdown: prev.action_breakdown.map((item, i) => 
            i === index ? { ...item, description: newPrompt } : item
          )
        }));
      } else if (promptType === "transition") {
        setExistingState(prev => ({
          ...prev,
          transition_prompts: prev.transition_prompts.map((item, i) => 
            i === index ? { ...item, prompt: newPrompt } : item
          )
        }));
      }
      
      setEditingPrompt(null);
      toast.success("Prompt updated");
    } catch (error) {
      toast.error("Failed to update prompt");
    }
  };

  // Select a version from carousel
  const handleSelectVersion = async (itemType, index, versionIndex) => {
    try {
      await api.post(`/extreme-action/scenes/${sceneId}/select-version`, {
        job_id: existingState?.id,
        item_type: itemType,
        index,
        version_index: versionIndex
      });
      
      await loadExistingState();
      toast.success("Version selected");
    } catch (error) {
      toast.error("Failed to select version");
    }
  };

  // Add reference image from asset library
  const handleAddRefFromLibrary = (assetUrl, note = "") => {
    const elemId = showAssetLibrary.elementId;
    if (!elemId) return;

    // Check if already added
    const current = elementRefs[elemId];
    if (current?.images?.some(img => img.url === assetUrl)) {
      toast.info("This image is already added");
      return;
    }

    setElementRefs(prev => ({
      ...prev,
      [elemId]: {
        ...prev[elemId],
        images: [...(prev[elemId]?.images || []), { url: assetUrl, note }]
      }
    }));
    
    toast.success("Reference image added");
  };

  // Upload and add reference image
  const handleUploadRef = async (file, elemId) => {
    try {
      const element = o1Elements.find(e => (e.character_profile_id || e.name) === elemId);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", `${element?.name || 'ref'}-extreme-${Date.now()}`);
      formData.append("type", "character");
      
      const res = await api.post(`/projects/${projectId}/assets`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      handleAddRefFromLibrary(res.data.file_url, "");
      toast.success("Image uploaded and added");
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  // Add additional reference (not attached to character)
  const handleAddAdditionalRef = (url, note = "", type = "image", filename = "") => {
    if (additionalRefs.some(r => r.url === url)) {
      toast.info("This reference is already added");
      return;
    }
    setAdditionalRefs(prev => [...prev, { url, note, type, filename }]);
    toast.success("Reference added");
  };

  // Upload additional reference
  const handleUploadAdditionalRef = async (file) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", `extreme-ref-${Date.now()}`);
      formData.append("type", file.type.startsWith("video/") ? "video" : "character");
      
      const res = await api.post(`/projects/${projectId}/assets`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      handleAddAdditionalRef(
        res.data.file_url, 
        "", 
        file.type.startsWith("video/") ? "video" : "image",
        file.name
      );
      toast.success("Reference uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  // Update note for element ref image
  const updateElementRefNote = (elemId, imageIndex, note) => {
    setElementRefs(prev => ({
      ...prev,
      [elemId]: {
        ...prev[elemId],
        images: prev[elemId].images.map((img, i) => 
          i === imageIndex ? { ...img, note } : img
        )
      }
    }));
    setEditingNote(null);
  };

  // Update note for additional ref
  const updateAdditionalRefNote = (index, note) => {
    setAdditionalRefs(prev => prev.map((ref, i) => 
      i === index ? { ...ref, note } : ref
    ));
    setEditingNote(null);
  };

  // Remove element ref image
  const removeElementRefImage = (elemId, imageIndex) => {
    setElementRefs(prev => ({
      ...prev,
      [elemId]: {
        ...prev[elemId],
        images: prev[elemId].images.filter((_, i) => i !== imageIndex)
      }
    }));
  };

  // Remove additional ref
  const removeAdditionalRef = (index) => {
    setAdditionalRefs(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden" data-testid="extreme-action-panel">
      <div className="p-3 border-b border-zinc-800 shrink-0">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-white flex items-center gap-2">
            <Zap className="w-4 h-4 text-orange-400" />
            Extreme Action Mode
          </h3>
          <Badge variant="outline" className="text-orange-400 border-orange-400/50 text-[10px]">
            Beta
          </Badge>
        </div>
        <p className="text-xs text-zinc-500 mt-1">
          For fight scenes, fast movement & precise choreography
        </p>
      </div>

      <ScrollArea className="flex-1 min-h-0">
        <div className="p-3 space-y-4">
          {/* How it works */}
          <div className="p-3 bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
              <div className="text-xs text-zinc-300 space-y-1">
                <p className="font-medium text-orange-300">How Extreme Action works:</p>
                <ol className="list-decimal list-inside space-y-0.5 text-zinc-400">
                  <li>We break your action into keyframe poses</li>
                  <li>Each pose is generated as a freeze-frame image</li>
                  <li>Slow-motion videos connect each pose</li>
                  <li>Final video is sped up for smooth action</li>
                </ol>
                <p className="text-zinc-500 italic pt-1">Result: Smooth action with consistent characters</p>
              </div>
            </div>
          </div>

          {/* Action Direction Preview */}
          {actionDirection ? (
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <Label className="text-xs text-zinc-400 mb-2 block">Action Direction</Label>
              <p className="text-sm text-zinc-300">{actionDirection}</p>
            </div>
          ) : (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-amber-400 text-xs">
                <AlertTriangle className="w-4 h-4" />
                <span>Enter action direction in Compose step first</span>
              </div>
            </div>
          )}

          {/* Character References with Library Modal */}
          {o1Elements.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-zinc-400">Character References</Label>
                <span className="text-[10px] text-zinc-500">{o1Elements.length} characters</span>
              </div>
              
              {o1Elements.map((elem) => {
                const elemId = elem.character_profile_id || elem.name;
                const elemData = elementRefs[elemId] || { images: [] };
                const isExpanded = expandedElement === elemId;
                
                return (
                  <div key={elemId} className="border border-zinc-700 rounded-lg overflow-hidden">
                    {/* Element Header */}
                    <button
                      onClick={() => setExpandedElement(isExpanded ? null : elemId)}
                      className="w-full flex items-center gap-2 p-2 bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                    >
                      {elem.frontal_image_url && (
                        <img 
                          src={getAssetUrl(elem.frontal_image_url)} 
                          alt={elem.name}
                          className="w-10 h-10 rounded object-cover"
                        />
                      )}
                      <div className="flex-1 text-left">
                        <span className="text-xs text-zinc-200 font-medium">{elem.name}</span>
                        <div className="flex items-center gap-1">
                          <Badge variant="outline" className="text-[9px] text-purple-400 border-purple-400/50">
                            {elemData.images?.length || 0} refs
                          </Badge>
                        </div>
                      </div>
                      {isExpanded ? <ChevronDown className="w-4 h-4 text-zinc-400" /> : <ChevronRight className="w-4 h-4 text-zinc-400" />}
                    </button>
                    
                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="p-2 space-y-2 bg-zinc-900/50">
                        {/* Existing ref images */}
                        {elemData.images?.length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                            {elemData.images.map((img, idx) => (
                              <div key={idx} className="relative group">
                                <img 
                                  src={getAssetUrl(img.url)} 
                                  alt={`Ref ${idx + 1}`}
                                  className="w-full aspect-square rounded object-cover"
                                />
                                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center gap-1">
                                  <button
                                    onClick={() => setEditingNote({ type: "element", elemId, idx })}
                                    className="p-1 bg-zinc-700 rounded hover:bg-zinc-600"
                                    title="Add note"
                                  >
                                    <StickyNote className="w-3 h-3 text-amber-400" />
                                  </button>
                                  <button
                                    onClick={() => removeElementRefImage(elemId, idx)}
                                    className="p-1 bg-zinc-700 rounded hover:bg-red-600"
                                    title="Remove"
                                  >
                                    <Trash2 className="w-3 h-3 text-red-400" />
                                  </button>
                                </div>
                                {img.note && (
                                  <div className="absolute bottom-0 left-0 right-0 bg-black/80 p-1 text-[9px] text-amber-300 truncate">
                                    {img.note}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Add from library button */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full text-xs border-dashed border-zinc-600 text-zinc-400 hover:text-white"
                          onClick={() => {
                            setShowAssetLibrary({ open: true, elementId: elemId, elementName: elem.name });
                            setLibraryViewMode('character');
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Reference Images
                        </Button>
                        
                        <p className="text-[10px] text-zinc-500">
                          Upload different angles of this character for better consistency
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Additional Reference Images/Videos */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-zinc-400">Additional References</Label>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-purple-400 hover:text-purple-300 h-6 px-2"
                onClick={() => setShowAdditionalRefsModal(true)}
              >
                <Plus className="w-3 h-3 mr-1" />
                Add
              </Button>
            </div>
            
            <p className="text-[10px] text-zinc-500">
              Add reference images or videos with notes to guide the generation
            </p>
            
            {additionalRefs.length > 0 && (
              <div className="space-y-2">
                {additionalRefs.map((ref, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 bg-zinc-800/50 rounded-lg">
                    <div className="w-16 h-16 shrink-0 rounded overflow-hidden bg-zinc-900">
                      {ref.type === "video" ? (
                        <div className="w-full h-full flex items-center justify-center">
                          <FileVideo className="w-6 h-6 text-purple-400" />
                        </div>
                      ) : (
                        <img 
                          src={getAssetUrl(ref.url)} 
                          alt={`Ref ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] text-zinc-500 truncate">{ref.filename || "Reference"}</p>
                      <div 
                        className="mt-1 text-xs text-zinc-300 cursor-pointer hover:text-white flex items-start gap-1"
                        onClick={() => setEditingNote({ type: "additional", idx })}
                      >
                        {ref.note ? (
                          <span>{ref.note}</span>
                        ) : (
                          <span className="text-zinc-500 italic flex items-center gap-1">
                            <Pencil className="w-3 h-3" />
                            Add note...
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeAdditionalRef(idx)}
                      className="p-1 hover:bg-zinc-700 rounded"
                    >
                      <X className="w-4 h-4 text-zinc-500 hover:text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {additionalRefs.length === 0 && (
              <button
                onClick={() => setShowAdditionalRefsModal(true)}
                className="w-full p-4 border border-dashed border-zinc-700 rounded-lg text-center hover:border-purple-500/50 transition-colors"
              >
                <Upload className="w-5 h-5 mx-auto text-zinc-500 mb-1" />
                <p className="text-xs text-zinc-400">Click to add reference images or videos</p>
                <p className="text-[10px] text-zinc-500">You can add notes to explain each reference</p>
              </button>
            )}
          </div>

          {/* Analysis Results */}
          {analysisResult && (
            <div className="border border-zinc-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowAnalysis(!showAnalysis)}
                className="w-full flex items-center justify-between p-2 text-xs text-zinc-400 hover:bg-zinc-800/50 transition-colors"
              >
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3 text-green-400" />
                  Analysis: {analysisResult.num_poses} poses, {analysisResult.duration_seconds}s
                  {analysisResult.duration_warning && (
                    <AlertTriangle className="w-3 h-3 text-amber-400 ml-1" />
                  )}
                </span>
                {showAnalysis ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
              
              {showAnalysis && (
                <div className="p-2 pt-0 space-y-2">
                  <div className="text-xs text-zinc-500">
                    Complexity: <span className={`font-medium ${
                      analysisResult.complexity_rating === 'high' ? 'text-red-400' :
                      analysisResult.complexity_rating === 'medium' ? 'text-amber-400' :
                      'text-green-400'
                    }`}>{analysisResult.complexity_rating}</span>
                  </div>
                  
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {analysisResult.breakdown?.map((item, i) => (
                      <div key={i} className="flex gap-2 text-xs p-1.5 bg-zinc-800/30 rounded">
                        <Badge variant="outline" className="text-[10px] shrink-0">{item.second}s</Badge>
                        <span className="text-zinc-400">{item.description}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t border-zinc-700">
                    <span className="text-xs text-zinc-500">Estimated cost:</span>
                    <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/50">
                      ~{analysisResult.estimated_cost_sparks} sparks
                    </Badge>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Pipeline Progress */}
          {generating && pipelineStatus && (
            <div className="p-3 bg-zinc-800/50 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-zinc-400">
                  Step {pipelineStatus.current_step}/5
                </span>
                <span className="text-xs font-medium text-orange-400">
                  {pipelineStatus.progress_percent}%
                </span>
              </div>
              
              <Progress value={pipelineStatus.progress_percent} className="h-2" />
              
              <p className="text-xs text-zinc-300">
                {pipelineStatus.progress_message}
              </p>
              
              {/* Show generated pose thumbnails */}
              {pipelineStatus.pose_images?.filter(p => p.status === 'complete').length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {pipelineStatus.pose_images.filter(p => p.status === 'complete').map((pose, i) => (
                    <div key={i} className="w-12 h-12 rounded overflow-hidden border border-green-500/50">
                      <img 
                        src={getAssetUrl(pose.image_url)} 
                        alt={`Pose ${pose.second}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Micro-Edit Panel (shows after first take) */}
          {showMicroEdit && existingState && (
            <Card className="bg-zinc-800/50 border-zinc-700">
              <CardHeader className="p-3 pb-1">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-zinc-300">Micro-Editor</span>
                    <Badge 
                      variant="outline" 
                      className={`text-[10px] ${
                        existingState.status === 'complete' ? 'text-green-400 border-green-400/50' :
                        existingState.status === 'paused' ? 'text-amber-400 border-amber-400/50' :
                        'text-zinc-400 border-zinc-600'
                      }`}
                    >
                      {existingState.status}
                    </Badge>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFullscreenEditor(true)}
                    className="h-6 w-6 p-0 text-zinc-400 hover:text-white hover:bg-zinc-700"
                    data-testid="expand-micro-editor-btn"
                    title="Expand to fullscreen"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-3 pt-2 space-y-2">
                {/* Final Video Preview */}
                {existingState.final_video_url && (
                  <div className="aspect-video rounded overflow-hidden bg-black mb-3">
                    <video
                      src={getAssetUrl(existingState.final_video_url)}
                      className="w-full h-full object-contain"
                      controls
                    />
                  </div>
                )}

                {/* Step 1: Action Breakdown Accordion */}
                <div className="border border-zinc-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setMicroEditExpanded(prev => ({ ...prev, breakdown: !prev.breakdown }))}
                    className="w-full flex items-center justify-between p-2 text-xs bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[9px] bg-blue-500/20 text-blue-400 border-blue-500/50">1</Badge>
                      <span className="text-zinc-300">Action Breakdown</span>
                      <Badge variant="outline" className="text-[9px] text-green-400 border-green-500/50">
                        {existingState.action_breakdown?.length || 0} poses
                      </Badge>
                    </span>
                    {microEditExpanded.breakdown ? <ChevronDown className="w-3 h-3 text-zinc-400" /> : <ChevronRight className="w-3 h-3 text-zinc-400" />}
                  </button>
                  {microEditExpanded.breakdown && (
                    <div className="p-2 space-y-1 bg-zinc-900/30">
                      {existingState.action_breakdown?.map((item, idx) => (
                        <div key={idx} className="flex gap-2 text-xs p-1.5 bg-zinc-800/30 rounded">
                          <Badge variant="outline" className="text-[10px] shrink-0">{item.second}s</Badge>
                          <span className="text-zinc-400 flex-1">{item.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Step 2: Pose Images Accordion */}
                <div className="border border-zinc-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setMicroEditExpanded(prev => ({ ...prev, poses: !prev.poses }))}
                    className="w-full flex items-center justify-between p-2 text-xs bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[9px] bg-purple-500/20 text-purple-400 border-purple-500/50">2</Badge>
                      <span className="text-zinc-300">Pose Images</span>
                      <Badge variant="outline" className={`text-[9px] ${
                        existingState.pose_images?.every(p => p.status === 'complete') 
                          ? 'text-green-400 border-green-500/50' 
                          : 'text-amber-400 border-amber-500/50'
                      }`}>
                        {existingState.pose_images?.filter(p => p.status === 'complete').length || 0}/{existingState.pose_images?.length || 0}
                      </Badge>
                    </span>
                    {microEditExpanded.poses ? <ChevronDown className="w-3 h-3 text-zinc-400" /> : <ChevronRight className="w-3 h-3 text-zinc-400" />}
                  </button>
                  {microEditExpanded.poses && (
                    <div className="p-2 space-y-2 bg-zinc-900/30">
                      <div className="grid grid-cols-4 gap-2">
                        {existingState.pose_images?.map((pose, idx) => (
                          <div key={idx} className="relative group">
                            {pose.status === 'complete' && pose.image_url ? (
                              <img 
                                src={getAssetUrl(pose.image_url)} 
                                alt={`Pose ${pose.second}s`}
                                className="w-full aspect-square rounded object-cover border border-green-500/50"
                              />
                            ) : (
                              <div className="w-full aspect-square rounded bg-zinc-800 border border-red-500/50 flex items-center justify-center">
                                <X className="w-4 h-4 text-red-400" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRegeneratePose(idx)}
                                disabled={regeneratingPose === idx}
                                className="text-[10px] h-6 px-2 text-white"
                              >
                                {regeneratingPose === idx ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-3 h-3" />
                                )}
                              </Button>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-[9px] text-center py-0.5 text-zinc-300">
                              {pose.second}s
                            </div>
                          </div>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRegenerateAllPoses}
                        disabled={regeneratingPose !== null}
                        className="w-full text-xs border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Regenerate All Poses
                      </Button>
                    </div>
                  )}
                </div>

                {/* Step 3: Transition Prompts Accordion */}
                <div className="border border-zinc-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setMicroEditExpanded(prev => ({ ...prev, prompts: !prev.prompts }))}
                    className="w-full flex items-center justify-between p-2 text-xs bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[9px] bg-cyan-500/20 text-cyan-400 border-cyan-500/50">3</Badge>
                      <span className="text-zinc-300">Transition Prompts</span>
                      <Badge variant="outline" className="text-[9px] text-green-400 border-green-500/50">
                        {existingState.transition_prompts?.length || 0}
                      </Badge>
                    </span>
                    {microEditExpanded.prompts ? <ChevronDown className="w-3 h-3 text-zinc-400" /> : <ChevronRight className="w-3 h-3 text-zinc-400" />}
                  </button>
                  {microEditExpanded.prompts && (
                    <div className="p-2 space-y-1 bg-zinc-900/30 max-h-40 overflow-y-auto">
                      {existingState.transition_prompts?.map((trans, idx) => (
                        <div key={idx} className="text-xs p-1.5 bg-zinc-800/30 rounded">
                          <div className="flex items-center gap-1 mb-1">
                            <Badge variant="outline" className="text-[9px]">{trans.from_second}s</Badge>
                            <span className="text-zinc-500">→</span>
                            <Badge variant="outline" className="text-[9px]">{trans.to_second}s</Badge>
                          </div>
                          <p className="text-zinc-400 text-[10px] line-clamp-2">{trans.prompt}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Step 4: Video Segments Accordion */}
                <div className="border border-zinc-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setMicroEditExpanded(prev => ({ ...prev, segments: !prev.segments }))}
                    className="w-full flex items-center justify-between p-2 text-xs bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[9px] bg-orange-500/20 text-orange-400 border-orange-500/50">4</Badge>
                      <span className="text-zinc-300">Video Segments</span>
                      <Badge variant="outline" className={`text-[9px] ${
                        existingState.video_segments?.every(s => s.status === 'complete') 
                          ? 'text-green-400 border-green-500/50' 
                          : 'text-amber-400 border-amber-500/50'
                      }`}>
                        {existingState.video_segments?.filter(s => s.status === 'complete').length || 0}/{existingState.video_segments?.length || 0}
                      </Badge>
                    </span>
                    {microEditExpanded.segments ? <ChevronDown className="w-3 h-3 text-zinc-400" /> : <ChevronRight className="w-3 h-3 text-zinc-400" />}
                  </button>
                  {microEditExpanded.segments && (
                    <div className="p-2 space-y-2 bg-zinc-900/30">
                      <div className="grid grid-cols-2 gap-2">
                        {existingState.video_segments?.map((seg, idx) => (
                          <div key={idx} className="relative group">
                            {seg.status === 'complete' && seg.video_url ? (
                              <video 
                                src={getAssetUrl(seg.video_url)}
                                className="w-full aspect-video rounded object-cover border border-green-500/50"
                                muted
                                onMouseEnter={(e) => e.target.play()}
                                onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                              />
                            ) : (
                              <div className="w-full aspect-video rounded bg-zinc-800 border border-red-500/50 flex items-center justify-center">
                                <X className="w-4 h-4 text-red-400" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRetrySegment(seg.segment_id)}
                                disabled={regeneratingSegment === seg.segment_id}
                                className="text-[10px] h-6 px-2 text-white"
                              >
                                {regeneratingSegment === seg.segment_id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <RefreshCw className="w-3 h-3" />
                                )}
                              </Button>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 bg-black/80 text-[9px] text-center py-0.5 text-zinc-300">
                              Seg {seg.segment_id + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Step 5: Final Compilation with Speed Control */}
                <div className="border border-zinc-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setMicroEditExpanded(prev => ({ ...prev, compile: !prev.compile }))}
                    className="w-full flex items-center justify-between p-2 text-xs bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                  >
                    <span className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[9px] bg-green-500/20 text-green-400 border-green-500/50">5</Badge>
                      <span className="text-zinc-300">Final Compilation</span>
                      <Badge variant="outline" className="text-[9px] text-orange-400 border-orange-500/50">
                        {speedMultiplier}X
                      </Badge>
                    </span>
                    {microEditExpanded.compile ? <ChevronDown className="w-3 h-3 text-zinc-400" /> : <ChevronRight className="w-3 h-3 text-zinc-400" />}
                  </button>
                  {microEditExpanded.compile && (
                    <div className="p-2 space-y-3 bg-zinc-900/30">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs text-zinc-400">Speed Multiplier</Label>
                          <span className="text-sm font-medium text-orange-400">{speedMultiplier}X</span>
                        </div>
                        <Slider
                          value={[speedMultiplier]}
                          onValueChange={([v]) => setSpeedMultiplier(v)}
                          min={0.5}
                          max={10}
                          step={0.5}
                          className="w-full"
                          data-testid="speed-multiplier-slider"
                        />
                        <div className="flex justify-between text-[10px] text-zinc-500">
                          <span>0.5X (Super Slow)</span>
                          <span>5X</span>
                          <span>10X (Fast)</span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        onClick={handleRecompile}
                        disabled={recompiling}
                        className="w-full text-xs border-green-500/50 text-green-400 hover:bg-green-500/10"
                      >
                        {recompiling ? (
                          <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Recompiling...</>
                        ) : (
                          <><RefreshCw className="w-3 h-3 mr-1" /> Recompile at {speedMultiplier}X</>
                        )}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-2 border-t border-zinc-700">
                  <span className="text-xs text-zinc-500">
                    Cost: {existingState.spark_cost_actual} sparks
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setExistingState(null);
                      setShowMicroEdit(false);
                    }}
                    className="text-xs text-zinc-400"
                  >
                    Start New
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </ScrollArea>

      {/* Action Buttons */}
      <div className="p-3 border-t border-zinc-800 bg-zinc-900/80 shrink-0 space-y-2">
        {!analysisResult ? (
          <Button
            onClick={handleAnalyze}
            disabled={analyzing || !actionDirection?.trim()}
            className="w-full bg-zinc-700 hover:bg-zinc-600 gap-2"
            data-testid="analyze-action-btn"
          >
            {analyzing ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</>
            ) : (
              <><Eye className="w-4 h-4" /> Analyze Action</>
            )}
          </Button>
        ) : (
          <Button
            onClick={() => handleStartPipeline(false)}
            disabled={generating}
            className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 gap-2"
            data-testid="generate-extreme-action-btn"
          >
            {generating ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {pipelineStatus?.progress_message?.slice(0, 30) || "Generating..."}</>
            ) : (
              <><Zap className="w-4 h-4" /> Generate Extreme Action (~{analysisResult.estimated_cost_sparks} sparks)</>
            )}
          </Button>
        )}
        
        {analysisResult && !generating && (
          <Button
            variant="ghost"
            onClick={handleAnalyze}
            className="w-full text-zinc-400 hover:text-white text-xs"
          >
            <RefreshCw className="w-3 h-3 mr-1" /> Re-analyze
          </Button>
        )}
      </div>

      {/* Character Reference Library Modal */}
      <Dialog 
        open={showAssetLibrary.open} 
        onOpenChange={(open) => {
          setShowAssetLibrary({ open, elementId: null, elementName: "" });
          setLibraryViewMode('character');
        }}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              Add Reference Image for {showAssetLibrary.elementName}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {libraryViewMode === 'character' 
                ? `Showing images from ${showAssetLibrary.elementName}'s folder`
                : 'Showing all assets from your library'
              }
            </DialogDescription>
          </DialogHeader>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 pb-2 border-b border-zinc-800">
            {libraryViewMode === 'all' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLibraryViewMode('character')}
                className="text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <ChevronLeft className="w-3 h-3 mr-1" />
                Back to folder
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setLibraryViewMode('all')}
                className="text-xs border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <FolderOpen className="w-3 h-3 mr-1" />
                Browse all assets
              </Button>
            )}
          </div>
          
          <div className="max-h-[400px] overflow-y-auto space-y-4 p-1">
            {showAssetLibrary.elementId && (() => {
              const element = o1Elements.find(e => (e.character_profile_id || e.name) === showAssetLibrary.elementId);
              const profile = getCharacterProfile(element);
              
              // Get profile reference images - sorted alphabetically
              const allProfileImages = (profile?.reference_images || [])
                .slice()
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              const currentRefs = elementRefs[showAssetLibrary.elementId]?.images || [];
              const currentRefUrls = currentRefs.map(r => r.url);
              
              const availableProfileImages = allProfileImages.filter(img => 
                img.file_url !== element?.frontal_image_url &&
                !currentRefUrls.includes(img.file_url)
              );
              
              // Find folder from frontal image
              const frontalAsset = assets.find(a => a.file_url === element?.frontal_image_url);
              const characterFolder = frontalAsset?.folder || profile?.name || element?.name;
              
              // Get assets from same folder - sorted alphabetically
              const characterFolderAssets = assets.filter(asset => {
                const isSameFolder = asset.folder?.toLowerCase() === characterFolder?.toLowerCase();
                const alreadyUsed = asset.file_url === element?.frontal_image_url ||
                                   currentRefUrls.includes(asset.file_url);
                return isSameFolder && !alreadyUsed;
              }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              
              // All other assets (for "all" view) - sorted alphabetically
              const allAvailableAssets = [...characterAssets, ...backgroundAssets].filter(asset => {
                const alreadyUsed = asset.file_url === element?.frontal_image_url ||
                                   currentRefUrls.includes(asset.file_url);
                return !alreadyUsed;
              }).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              
              if (libraryViewMode === 'character') {
                const hasProfileImages = availableProfileImages.length > 0;
                const hasFolderAssets = characterFolderAssets.length > 0;
                const totalImages = availableProfileImages.length + characterFolderAssets.length;
                
                return (
                  <div className="space-y-4">
                    {/* Profile Images */}
                    {hasProfileImages && (
                      <div>
                        <p className="text-xs text-zinc-400 mb-2 font-medium flex items-center gap-2">
                          <Star className="w-3 h-3 text-indigo-400" />
                          Character Profile ({availableProfileImages.length})
                        </p>
                        <div className="grid grid-cols-4 gap-3">
                          {availableProfileImages.map((img, idx) => (
                            <button
                              key={`profile-${idx}`}
                              onClick={() => handleAddRefFromLibrary(img.file_url)}
                              className="aspect-square rounded-lg overflow-hidden border-2 border-indigo-500/50 hover:border-indigo-500 transition-colors"
                              title={img.name || `Profile Image ${idx + 1}`}
                            >
                              <img
                                src={getAssetUrl(img.thumbnail_url || img.file_url)}
                                alt={img.name || `Profile ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Folder Assets */}
                    {hasFolderAssets && (
                      <div>
                        <p className="text-xs text-zinc-400 mb-2 font-medium flex items-center gap-2">
                          <FolderOpen className="w-3 h-3 text-purple-400" />
                          {characterFolder} Folder ({characterFolderAssets.length})
                        </p>
                        <div className="grid grid-cols-4 gap-3">
                          {characterFolderAssets.map((asset, idx) => (
                            <button
                              key={`folder-${idx}`}
                              onClick={() => handleAddRefFromLibrary(asset.file_url)}
                              className="aspect-square rounded-lg overflow-hidden border border-purple-500/50 hover:border-purple-500 transition-colors"
                              title={asset.name}
                            >
                              <img
                                src={getAssetUrl(asset.thumbnail_url || asset.file_url)}
                                alt={asset.name}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Empty state */}
                    {totalImages === 0 && (
                      <div className="text-center py-8">
                        <Users className="w-8 h-8 mx-auto text-zinc-600 mb-2" />
                        <p className="text-zinc-500 text-sm mb-3">
                          No additional images found for {element?.name}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setLibraryViewMode('all')}
                          className="text-xs border-zinc-700"
                        >
                          Browse all assets
                        </Button>
                      </div>
                    )}
                  </div>
                );
              } else {
                // All Assets View
                return (
                  <div>
                    <p className="text-xs text-zinc-400 mb-2 font-medium flex items-center gap-2">
                      <FolderOpen className="w-3 h-3" />
                      All Assets ({allAvailableAssets.length})
                    </p>
                    {allAvailableAssets.length > 0 ? (
                      <div className="grid grid-cols-4 gap-3">
                        {allAvailableAssets.map((asset, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleAddRefFromLibrary(asset.file_url)}
                            className={`aspect-square rounded-lg overflow-hidden border transition-colors ${
                              asset.type === 'character' 
                                ? 'border-purple-500/30 hover:border-purple-500' 
                                : 'border-green-500/30 hover:border-green-500'
                            }`}
                            title={asset.name}
                          >
                            <img
                              src={getAssetUrl(asset.thumbnail_url || asset.file_url)}
                              alt={asset.name}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-zinc-500 text-sm text-center py-8">
                        No available assets in library
                      </p>
                    )}
                  </div>
                );
              }
            })()}
            
            {/* Upload Option */}
            <div className="border-t border-zinc-800 pt-4">
              <p className="text-xs text-zinc-400 mb-2 font-medium">Upload New Image</p>
              <label className="flex items-center justify-center w-full h-20 rounded-lg border-2 border-dashed border-zinc-700 hover:border-purple-500 transition-colors cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file && showAssetLibrary.elementId) {
                      await handleUploadRef(file, showAssetLibrary.elementId);
                    }
                    e.target.value = "";
                  }}
                />
                <div className="text-center">
                  <Upload className="w-5 h-5 mx-auto text-zinc-500 mb-1" />
                  <span className="text-xs text-zinc-500">Click to upload a new image</span>
                </div>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Additional References Modal */}
      <Dialog open={showAdditionalRefsModal} onOpenChange={setShowAdditionalRefsModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">Add Additional Reference</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Add images or videos with notes to guide the generation
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto space-y-4 p-1">
            {/* Available assets */}
            <div>
              <p className="text-xs text-zinc-400 mb-2 font-medium flex items-center gap-2">
                <FolderOpen className="w-3 h-3" />
                From Asset Library ({allAssets.length} assets)
              </p>
              {allAssets.length > 0 ? (
                <div className="grid grid-cols-4 gap-3 max-h-60 overflow-y-auto">
                  {allAssets.filter(a => 
                    !additionalRefs.some(r => r.url === a.file_url)
                  ).map((asset, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleAddAdditionalRef(asset.file_url, "", "image", asset.name)}
                      className={`aspect-square rounded-lg overflow-hidden border transition-colors ${
                        asset.type === 'character' ? 'border-purple-500/30 hover:border-purple-500' :
                        asset.type === 'background' ? 'border-green-500/30 hover:border-green-500' :
                        asset.type === 'set' ? 'border-blue-500/30 hover:border-blue-500' :
                        'border-zinc-600 hover:border-zinc-400'
                      }`}
                      title={`${asset.name} (${asset.type})`}
                    >
                      <img
                        src={getAssetUrl(asset.thumbnail_url || asset.file_url)}
                        alt={asset.name}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm text-center py-4">
                  No assets in library
                </p>
              )}
            </div>
            
            {/* Upload Option */}
            <div className="border-t border-zinc-800 pt-4">
              <p className="text-xs text-zinc-400 mb-2 font-medium">Upload New</p>
              <label className="flex items-center justify-center w-full h-20 rounded-lg border-2 border-dashed border-zinc-700 hover:border-purple-500 transition-colors cursor-pointer">
                <input
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      await handleUploadAdditionalRef(file);
                    }
                    e.target.value = "";
                  }}
                />
                <div className="text-center">
                  <Upload className="w-5 h-5 mx-auto text-zinc-500 mb-1" />
                  <span className="text-xs text-zinc-500">Click to upload image or video</span>
                </div>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duration Warning Dialog */}
      <Dialog open={showDurationConfirm} onOpenChange={setShowDurationConfirm}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              Long Action Scene
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-zinc-400 space-y-2 pt-2">
                <p>
                  This action will take <strong className="text-amber-400">{analysisResult?.duration_seconds} seconds</strong>, 
                  which is longer than the recommended 5 seconds.
                </p>
                <p className="text-sm">
                  Longer scenes are more expensive and may have quality inconsistencies. 
                  Consider breaking it into multiple shorter shots.
                </p>
                <div className="flex items-center justify-between p-2 bg-zinc-800 rounded mt-3">
                  <span>Estimated cost:</span>
                  <Badge className="bg-orange-500/20 text-orange-400">
                    ~{analysisResult?.estimated_cost_sparks} sparks
                  </Badge>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowDurationConfirm(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleStartPipeline(true)}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Proceed Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Note Dialog for Element Refs */}
      {editingNote?.type === "element" && (
        <Dialog open={true} onOpenChange={() => setEditingNote(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Add Note to Reference</DialogTitle>
              <DialogDescription>
                Describe what this reference image shows (e.g., &quot;side profile&quot;, &quot;action pose&quot;, &quot;fighting stance&quot;)
              </DialogDescription>
            </DialogHeader>
            <Textarea
              autoFocus
              placeholder="e.g., Character from the side showing their profile and costume details"
              defaultValue={elementRefs[editingNote.elemId]?.images[editingNote.idx]?.note || ""}
              className="min-h-[100px] bg-zinc-800 border-zinc-700"
              id="note-input"
            />
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setEditingNote(null)}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  const input = document.getElementById("note-input");
                  updateElementRefNote(editingNote.elemId, editingNote.idx, input?.value || "");
                }}
                className="bg-purple-500 hover:bg-purple-600"
              >
                Save Note
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Note Dialog for Additional Refs */}
      {editingNote?.type === "additional" && (
        <Dialog open={true} onOpenChange={() => setEditingNote(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Add Note to Reference</DialogTitle>
              <DialogDescription>
                Describe what this reference shows and how it should be used
              </DialogDescription>
            </DialogHeader>
            <Textarea
              autoFocus
              placeholder="e.g., Reference for the fighting style - notice the wide stance and raised fists"
              defaultValue={additionalRefs[editingNote.idx]?.note || ""}
              className="min-h-[100px] bg-zinc-800 border-zinc-700"
              id="additional-note-input"
            />
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setEditingNote(null)}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button 
                onClick={() => {
                  const input = document.getElementById("additional-note-input");
                  updateAdditionalRefNote(editingNote.idx, input?.value || "");
                }}
                className="bg-purple-500 hover:bg-purple-600"
              >
                Save Note
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Fullscreen Micro-Editor Modal - Redesigned */}
      <Dialog open={showFullscreenEditor} onOpenChange={setShowFullscreenEditor}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] w-[95vw] h-[90vh] max-h-[90vh] p-0 flex flex-col">
          <DialogTitle className="sr-only">Micro Editor</DialogTitle>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800 shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-orange-400" />
                Micro Editor
              </h2>
              <Badge 
                variant="outline" 
                className={`text-xs ${
                  existingState?.status === 'complete' ? 'text-green-400 border-green-400/50' :
                  existingState?.status === 'paused' ? 'text-amber-400 border-amber-400/50' :
                  'text-zinc-400 border-zinc-600'
                }`}
              >
                {existingState?.status}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowFullscreenEditor(false)}
              className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
              data-testid="minimize-micro-editor-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Main Content - Single Column */}
          <ScrollArea className="flex-1">
            <div className="p-6 space-y-6 max-w-6xl mx-auto">
              
              {/* Pose Images Section */}
              <div className="border border-zinc-700 rounded-xl overflow-hidden bg-zinc-800/30">
                <button
                  onClick={() => setMicroEditExpanded(prev => ({ ...prev, poses: !prev.poses }))}
                  className="w-full flex items-center justify-between p-4 text-sm bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                >
                  <span className="flex items-center gap-3">
                    <ImageIcon className="w-5 h-5 text-purple-400" />
                    <span className="text-zinc-100 font-medium text-base">Pose Images</span>
                    <Badge variant="outline" className={`text-xs ${
                      existingState?.pose_images?.every(p => p.status === 'complete') 
                        ? 'text-green-400 border-green-500/50' 
                        : 'text-amber-400 border-amber-500/50'
                    }`}>
                      {existingState?.pose_images?.filter(p => p.status === 'complete').length || 0}/{existingState?.pose_images?.length || 0}
                    </Badge>
                  </span>
                  {microEditExpanded.poses ? <ChevronDown className="w-5 h-5 text-zinc-400" /> : <ChevronRight className="w-5 h-5 text-zinc-400" />}
                </button>
                
                {microEditExpanded.poses && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {existingState?.pose_images?.map((pose, idx) => {
                        const versions = pose?.versions || [];
                        const hasMultipleVersions = versions.length > 1;
                        const currentVersionIdx = poseVersionIndices[idx] || 0;
                        const displayUrl = getPoseImageUrl(pose, idx);
                        const currentPrompt = editedPosePrompts[idx] ?? existingState?.action_breakdown?.[idx]?.description ?? "";
                        
                        return (
                          <div key={idx} className="bg-zinc-900/50 rounded-lg border border-zinc-700/50 overflow-hidden">
                            {/* Image with Carousel */}
                            <div className="relative aspect-video bg-zinc-950">
                              {pose.status === 'complete' && displayUrl ? (
                                <img 
                                  src={getAssetUrl(displayUrl)} 
                                  alt={`Pose ${idx + 1}`}
                                  className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                  onClick={() => setFullscreenImage(displayUrl)}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <X className="w-8 h-8 text-red-400" />
                                </div>
                              )}
                              
                              {/* Pose number badge */}
                              <div className="absolute top-2 left-2">
                                <Badge className="bg-purple-500/90 text-white border-0 text-xs font-medium">
                                  Pose {idx + 1}
                                </Badge>
                              </div>

                              {/* Download and Upload buttons */}
                              <div className="absolute top-2 right-2 flex gap-1">
                                {pose.status === 'complete' && displayUrl && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleDownloadPoseImage(displayUrl, idx); }}
                                    className="p-1.5 bg-black/70 hover:bg-green-500/80 rounded-md transition-colors"
                                    title="Download image"
                                  >
                                    <Download className="w-4 h-4 text-white" />
                                  </button>
                                )}
                                <label
                                  className="p-1.5 bg-black/70 hover:bg-blue-500/80 rounded-md transition-colors cursor-pointer"
                                  title="Upload custom image"
                                >
                                  <Upload className="w-4 h-4 text-white" />
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleUploadPoseImage(file, idx);
                                      e.target.value = '';
                                    }}
                                  />
                                </label>
                              </div>
                              
                              {/* Version carousel arrows */}
                              {hasMultipleVersions && (
                                <>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handlePoseVersionChange(idx, 'prev'); }}
                                    className="absolute left-1 top-1/2 -translate-y-1/2 p-1 bg-black/70 hover:bg-black/90 rounded-full transition-colors"
                                  >
                                    <ChevronLeft className="w-4 h-4 text-white" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handlePoseVersionChange(idx, 'next'); }}
                                    className="absolute right-1 top-1/2 -translate-y-1/2 p-1 bg-black/70 hover:bg-black/90 rounded-full transition-colors"
                                  >
                                    <ChevronRight className="w-4 h-4 text-white" />
                                  </button>
                                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 px-2 py-0.5 rounded-full">
                                    <span className="text-[10px] text-white">
                                      {currentVersionIdx + 1} / {versions.length}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            
                            {/* Editable Prompt */}
                            <div className="p-3 space-y-2">
                              <Textarea
                                value={currentPrompt}
                                onChange={(e) => setEditedPosePrompts(prev => ({ ...prev, [idx]: e.target.value }))}
                                className="min-h-[60px] text-xs bg-zinc-800 border-zinc-700 resize-none"
                                placeholder="Describe this pose..."
                              />
                              <Button
                                size="sm"
                                onClick={() => handleRegeneratePose(idx, currentPrompt)}
                                disabled={regeneratingPose === idx}
                                className="w-full text-xs bg-purple-600 hover:bg-purple-700"
                              >
                                {regeneratingPose === idx ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Regenerating...</>
                                ) : (
                                  <><RefreshCw className="w-3 h-3 mr-1" /> Regenerate</>
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Generate New Video Segments Button - replaced by Set up animation button */}
                    {/* Set up animation Button - shows when poses are complete but animation not set up */}
                    {(existingState?.status === 'poses_complete' || 
                      (existingState?.pose_images?.every(p => p.status === 'complete') && 
                       (!existingState?.transition_prompts || existingState?.transition_prompts.length === 0))) && (
                      <Button
                        onClick={handleSetupAnimation}
                        disabled={settingUpAnimation}
                        className="w-full bg-gradient-to-r from-purple-500 to-orange-500 hover:from-purple-600 hover:to-orange-600 text-white font-medium py-3"
                        data-testid="setup-animation-btn"
                      >
                        {settingUpAnimation ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up animation...</>
                        ) : (
                          <><Settings2 className="w-4 h-4 mr-2" /> Set up animation</>
                        )}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Animation Set Up Section - New workflow */}
              {(existingState?.transition_prompts?.length > 0 || existingState?.status === 'animation_setup') && (
                <div id="animation-setup-section" className="border border-zinc-700 rounded-xl overflow-hidden bg-zinc-800/30">
                  {/* Global Progress Bar - Shows when any segment is generating */}
                  {generatingSegments.size > 0 && (
                    <div className="w-full h-2 bg-zinc-800">
                      <div 
                        className="h-full bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 animate-pulse transition-all duration-300"
                        style={{ 
                          width: `${Math.max(...Object.values(segmentProgress), 0)}%`,
                          backgroundSize: '200% 100%',
                          animation: 'shimmer 2s ease-in-out infinite'
                        }}
                      />
                    </div>
                  )}
                  
                  <button
                    onClick={() => setMicroEditExpanded(prev => ({ ...prev, animationSetup: !prev.animationSetup }))}
                    className="w-full flex items-center justify-between p-4 text-sm bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                  >
                    <span className="flex items-center gap-3">
                      <Film className="w-5 h-5 text-orange-400" />
                      <span className="text-zinc-100 font-medium text-base">Animation Set Up</span>
                      {generatingSegments.size > 0 && (
                        <Badge variant="outline" className="text-orange-400 border-orange-500/50 text-xs animate-pulse">
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" /> 
                          {generatingSegments.size} generating...
                        </Badge>
                      )}
                      <Badge variant="outline" className={`text-xs ${
                        existingState?.video_segments?.filter(s => s.status === 'complete').length === existingState?.video_segments?.length
                          ? 'text-green-400 border-green-500/50' 
                          : 'text-amber-400 border-amber-500/50'
                      }`}>
                        {existingState?.video_segments?.filter(s => s.status === 'complete').length || 0}/{existingState?.video_segments?.length || 0} animated
                      </Badge>
                    </span>
                    {microEditExpanded.animationSetup ? <ChevronDown className="w-5 h-5 text-zinc-400" /> : <ChevronRight className="w-5 h-5 text-zinc-400" />}
                  </button>
                  
                  {microEditExpanded.animationSetup && (
                    <div className="p-4 space-y-6">
                      {/* Segment Rows */}
                      {existingState?.video_segments?.map((segment, idx) => {
                        const startPose = existingState?.pose_images?.[idx];
                        const endPose = existingState?.pose_images?.[idx + 1];
                        const versions = segment?.versions || [];
                        const hasMultipleVersions = versions.length > 1;
                        const currentVersionIdx = segmentVersionIndices[idx] || 0;
                        const currentVersion = versions[currentVersionIdx];
                        const displayVideoUrl = currentVersion?.video_url || segment?.video_url;
                        const displayPrompt = editedSegmentPrompts[idx] ?? currentVersion?.prompt_used ?? segment?.prompt ?? "";
                        const hasVideo = segment?.status === 'complete' && displayVideoUrl;
                        const isGenerating = generatingSegments.has(idx) || segment?.status === 'generating';
                        const progress = segmentProgress[idx] || 0;
                        
                        return (
                          <div 
                            key={idx} 
                            className="bg-zinc-900/50 rounded-xl border border-zinc-700/50 overflow-hidden"
                          >
                            {/* Progress Bar - Full width at top when generating */}
                            {isGenerating && (
                              <div className="w-full h-1.5 bg-zinc-800">
                                <div 
                                  className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-200 ease-out"
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                            )}
                            
                            {/* Segment Header with Version Navigation */}
                            <div className="flex items-center justify-between p-3 bg-zinc-800/50 border-b border-zinc-700/50">
                              <div className="flex items-center gap-2">
                                <Badge className="bg-orange-500/90 text-white border-0 text-xs font-medium">
                                  Segment {idx + 1}
                                </Badge>
                                {isGenerating && (
                                  <Badge variant="outline" className="text-orange-400 border-orange-500/50 text-xs animate-pulse">
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" /> {Math.round(progress)}%
                                  </Badge>
                                )}
                                {!isGenerating && segment?.status === 'complete' && (
                                  <Badge variant="outline" className="text-green-400 border-green-500/50 text-xs">
                                    <Check className="w-3 h-3 mr-1" /> Ready
                                  </Badge>
                                )}
                                {segment?.status === 'error' && (
                                  <Badge variant="outline" className="text-red-400 border-red-500/50 text-xs">
                                    <AlertTriangle className="w-3 h-3 mr-1" /> Error
                                  </Badge>
                                )}
                              </div>
                              
                              {/* Version Navigation */}
                              {hasMultipleVersions && (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleSegmentVersionChange(idx, 'prev')}
                                    className="p-1 bg-zinc-700 hover:bg-zinc-600 rounded transition-colors"
                                  >
                                    <ChevronLeft className="w-4 h-4 text-white" />
                                  </button>
                                  <span className="text-xs text-zinc-300 min-w-[40px] text-center">
                                    {currentVersionIdx + 1} / {versions.length}
                                  </span>
                                  <button
                                    onClick={() => handleSegmentVersionChange(idx, 'next')}
                                    className="p-1 bg-zinc-700 hover:bg-zinc-600 rounded transition-colors"
                                  >
                                    <ChevronRight className="w-4 h-4 text-white" />
                                  </button>
                                </div>
                              )}
                            </div>
                            
                            {/* Start Image | Video | End Image Row */}
                            <div className="p-4">
                              <div className="grid grid-cols-3 gap-4 mb-4">
                                {/* Start Image */}
                                <div className="space-y-2">
                                  <div 
                                    className="aspect-video bg-zinc-950 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-purple-500/50 transition-all"
                                    onClick={() => startPose?.image_url && setFullscreenImage(startPose.image_url)}
                                  >
                                    {startPose?.image_url ? (
                                      <img 
                                        src={getAssetUrl(startPose.image_url)} 
                                        alt={`Pose ${idx + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-zinc-500">
                                        <ImageIcon className="w-8 h-8" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-center">
                                    <Badge className="bg-purple-500/80 text-white border-0 text-xs">
                                      Start Image
                                    </Badge>
                                  </div>
                                </div>
                                
                                {/* Video (or placeholder) */}
                                <div className="space-y-2">
                                  <div className="aspect-video bg-zinc-950 rounded-lg overflow-hidden relative">
                                    {hasVideo ? (
                                      <>
                                        <video 
                                          src={getAssetUrl(displayVideoUrl)}
                                          className="w-full h-full object-cover"
                                          muted
                                          loop
                                          onMouseEnter={(e) => e.target.play()}
                                          onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                                        />
                                        <button
                                          onClick={() => setPlayingVideo(displayVideoUrl)}
                                          className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity"
                                        >
                                          <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                            <Play className="w-6 h-6 text-white ml-1" />
                                          </div>
                                        </button>
                                      </>
                                    ) : isGenerating ? (
                                      <div className="w-full h-full flex flex-col items-center justify-center text-orange-400">
                                        <Loader2 className="w-8 h-8 animate-spin mb-2" />
                                        <span className="text-xs">Generating...</span>
                                      </div>
                                    ) : (
                                      <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500">
                                        <Film className="w-8 h-8 mb-2 opacity-50" />
                                        <span className="text-xs">Not generated yet</span>
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-center">
                                    <Badge className="bg-orange-500/80 text-white border-0 text-xs">
                                      Video
                                    </Badge>
                                  </div>
                                </div>
                                
                                {/* End Image */}
                                <div className="space-y-2">
                                  <div 
                                    className="aspect-video bg-zinc-950 rounded-lg overflow-hidden cursor-pointer hover:ring-2 hover:ring-purple-500/50 transition-all"
                                    onClick={() => endPose?.image_url && setFullscreenImage(endPose.image_url)}
                                  >
                                    {endPose?.image_url ? (
                                      <img 
                                        src={getAssetUrl(endPose.image_url)} 
                                        alt={`Pose ${idx + 2}`}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-zinc-500">
                                        <ImageIcon className="w-8 h-8" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-center">
                                    <Badge className="bg-purple-500/80 text-white border-0 text-xs">
                                      Tail Image
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Editable Prompt */}
                              <div className="space-y-3">
                                <Textarea
                                  value={displayPrompt}
                                  onChange={(e) => setEditedSegmentPrompts(prev => ({ ...prev, [idx]: e.target.value }))}
                                  onBlur={(e) => handleUpdateSegmentPrompt(idx, e.target.value)}
                                  className="min-h-[80px] text-sm bg-zinc-800 border-zinc-700 resize-none"
                                  placeholder="Prompt to create the video that connects the start and tail images..."
                                  disabled={isGenerating}
                                />
                                
                                {/* Animate Button - allows parallel generation */}
                                <Button
                                  onClick={() => handleAnimateSegment(idx)}
                                  disabled={isGenerating}
                                  className={`w-full font-medium py-2.5 ${
                                    isGenerating
                                      ? 'bg-zinc-700 text-zinc-400 cursor-not-allowed'
                                      : hasVideo 
                                        ? 'bg-zinc-700 hover:bg-zinc-600 text-white' 
                                        : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white'
                                  }`}
                                  data-testid={`animate-segment-${idx}-btn`}
                                >
                                  {isGenerating ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating ({Math.round(progress)}%)...</>
                                  ) : hasVideo ? (
                                    <><RefreshCw className="w-4 h-4 mr-2" /> Let&apos;s go again</>
                                  ) : (
                                    <><Sparkles className="w-4 h-4 mr-2" /> Animate</>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      
                      {/* Edit & Compile Button - shows when at least one segment is complete */}
                      {existingState?.video_segments?.some(s => s.status === 'complete') && (
                        <div className="pt-4 border-t border-zinc-700">
                          {/* Warning if some segments missing */}
                          {!existingState?.video_segments?.every(s => s.status === 'complete') && (
                            <div className="flex items-center gap-2 p-3 mb-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                              <span className="text-sm text-amber-300">
                                Some segments have not been animated yet. Generate all segments for the best result.
                              </span>
                            </div>
                          )}
                          
                          <Button
                            onClick={handleOpenSegmentEditor}
                            disabled={compiling}
                            className="w-full bg-gradient-to-r from-purple-500 to-orange-500 hover:from-purple-600 hover:to-orange-600 text-white font-medium py-3"
                            data-testid="edit-compile-btn"
                          >
                            <Sliders className="w-4 h-4 mr-2" />
                            Edit & Compile
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Legacy Video Segments Section - Hidden, replaced by Animation Set Up */}
              {false && (
              <div id="video-segments-section" className="border border-zinc-700 rounded-xl overflow-hidden bg-zinc-800/30">
                {/* Section Header */}
                {(() => {
                  const hasVideos = existingState?.video_segments && existingState.video_segments.length > 0;
                  const canExpand = hasVideos;
                  
                  return (
                    <button
                      onClick={() => canExpand && setMicroEditExpanded(prev => ({ ...prev, segments: !prev.segments }))}
                      className={`w-full flex items-center justify-between p-4 text-sm bg-zinc-800/50 transition-colors ${
                        canExpand ? 'hover:bg-zinc-800 cursor-pointer' : 'cursor-not-allowed opacity-60'
                      }`}
                      disabled={!canExpand}
                    >
                      <span className="flex items-center gap-3">
                        <Film className={`w-5 h-5 ${hasVideos ? 'text-orange-400' : 'text-zinc-500'}`} />
                        <span className={`font-medium text-base ${hasVideos ? 'text-zinc-100' : 'text-zinc-500'}`}>
                          Video Segments
                        </span>
                        {hasVideos ? (
                          <Badge variant="outline" className={`text-xs ${
                            existingState?.video_segments?.every(s => s.status === 'complete') 
                              ? 'text-green-400 border-green-500/50' 
                              : 'text-amber-400 border-amber-500/50'
                          }`}>
                            {existingState?.video_segments?.filter(s => s.status === 'complete').length || 0}/{existingState?.video_segments?.length || 0}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs text-zinc-500 border-zinc-600">
                            Click Animate to generate
                          </Badge>
                        )}
                      </span>
                      {canExpand && (
                        microEditExpanded.segments ? <ChevronDown className="w-5 h-5 text-zinc-400" /> : <ChevronRight className="w-5 h-5 text-zinc-400" />
                      )}
                    </button>
                  );
                })()}
                
                {microEditExpanded.segments && existingState?.video_segments?.length > 0 && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {existingState?.video_segments?.map((seg, idx) => {
                        const versions = seg?.versions || [];
                        const hasMultipleVersions = versions.length > 1;
                        const currentVersionIdx = segmentVersionIndices[idx] || 0;
                        const displayUrl = getSegmentVideoUrl(seg, idx);
                        const currentPrompt = editedSegmentPrompts[idx] ?? existingState?.transition_prompts?.[idx]?.prompt ?? seg.prompt ?? "";
                        
                        return (
                          <div key={idx} className="bg-zinc-900/50 rounded-lg border border-zinc-700/50 overflow-hidden">
                            {/* Video with Carousel */}
                            <div className="relative aspect-video bg-zinc-950">
                              {seg.status === 'complete' && displayUrl ? (
                                <>
                                  <video 
                                    src={getAssetUrl(displayUrl)}
                                    className="w-full h-full object-cover"
                                    muted
                                    loop
                                    onMouseEnter={(e) => e.target.play()}
                                    onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                                  />
                                  <button
                                    onClick={() => setPlayingVideo(displayUrl)}
                                    className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity"
                                  >
                                    <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                      <Play className="w-7 h-7 text-white ml-1" />
                                    </div>
                                  </button>
                                </>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <X className="w-8 h-8 text-red-400" />
                                </div>
                              )}
                              
                              {/* Segment number badge */}
                              <div className="absolute top-2 left-2">
                                <Badge className="bg-orange-500/90 text-white border-0 text-xs font-medium">
                                  Segment {idx + 1}
                                </Badge>
                              </div>
                              
                              {/* Download button */}
                              {seg.status === 'complete' && displayUrl && (
                                <button
                                  onClick={() => handleDownloadSegment(displayUrl, idx)}
                                  className="absolute top-2 right-2 p-1.5 bg-black/70 hover:bg-green-500/80 rounded-md transition-colors"
                                  title="Download segment"
                                >
                                  <Download className="w-4 h-4 text-white" />
                                </button>
                              )}
                              
                              {/* Version carousel arrows */}
                              {hasMultipleVersions && (
                                <>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleSegmentVersionChange(idx, 'prev'); }}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/70 hover:bg-black/90 rounded-full transition-colors"
                                  >
                                    <ChevronLeft className="w-5 h-5 text-white" />
                                  </button>
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleSegmentVersionChange(idx, 'next'); }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-black/70 hover:bg-black/90 rounded-full transition-colors"
                                  >
                                    <ChevronRight className="w-5 h-5 text-white" />
                                  </button>
                                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 px-2 py-0.5 rounded-full">
                                    <span className="text-xs text-white">
                                      {currentVersionIdx + 1} / {versions.length}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            
                            {/* Editable Prompt */}
                            <div className="p-3 space-y-2">
                              <Textarea
                                value={currentPrompt}
                                onChange={(e) => setEditedSegmentPrompts(prev => ({ ...prev, [idx]: e.target.value }))}
                                className="min-h-[60px] text-xs bg-zinc-800 border-zinc-700 resize-none"
                                placeholder="Describe this transition..."
                              />
                              <Button
                                size="sm"
                                onClick={() => handleRegenerateSegment(idx, currentPrompt)}
                                disabled={regeneratingSegment === idx}
                                className="w-full text-xs bg-orange-600 hover:bg-orange-700"
                              >
                                {regeneratingSegment === idx ? (
                                  <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Regenerating...</>
                                ) : (
                                  <><RefreshCw className="w-3 h-3 mr-1" /> Regenerate</>
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Edit & Compile Button - shows when segments are complete */}
                    {existingState?.video_segments?.every(s => s.status === 'complete') && existingState?.status !== 'complete' && (
                      <div className="space-y-3 pt-2">
                        <Button
                          onClick={handleOpenSegmentEditor}
                          disabled={compiling}
                          className="w-full bg-gradient-to-r from-purple-500 to-orange-500 hover:from-purple-600 hover:to-orange-600 text-white font-medium py-3"
                          data-testid="edit-compile-btn"
                        >
                          <Sliders className="w-4 h-4 mr-2" />
                          Edit & Compile
                        </Button>
                        
                        {/* Progress bar during compilation */}
                        {compiling && pipelineStatus && (
                          <div className="space-y-2 p-3 bg-zinc-800/50 rounded-lg">
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-zinc-400">{pipelineStatus.progress_message}</span>
                              <span className="text-orange-400 font-medium">{pipelineStatus.progress_percent}%</span>
                            </div>
                            <div className="w-full h-2 bg-zinc-700 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all duration-500"
                                style={{ width: `${pipelineStatus.progress_percent}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              {/* Final Video Section */}
              {existingState?.final_video_url && (
                <div id="final-video-section" className="border border-green-500/50 rounded-xl overflow-hidden bg-zinc-800/30 p-4">
                  <h3 className="text-base font-medium text-zinc-100 mb-3 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-green-400" />
                    Final Shot
                    <Badge className="ml-2 bg-green-500/20 text-green-400 border-green-500/50">
                      {existingState.speed_multiplier || speedMultiplier}X Speed
                    </Badge>
                  </h3>
                  <div className="aspect-video rounded-lg overflow-hidden bg-black max-w-3xl mx-auto mb-4">
                    <video
                      src={getAssetUrl(existingState.final_video_url)}
                      className="w-full h-full object-contain"
                      controls
                    />
                  </div>
                  
                  {/* Review Animation Button */}
                  <Button
                    onClick={handleReviewAnimation}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-medium py-3"
                    data-testid="review-animation-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Review Animation
                  </Button>
                </div>
              )}

            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Fullscreen Image View Modal */}
      <Dialog open={!!fullscreenImage} onOpenChange={() => setFullscreenImage(null)}>
        <DialogContent className="bg-black/95 border-zinc-800 max-w-[95vw] w-[95vw] h-[95vh] max-h-[95vh] p-0 flex items-center justify-center">
          <DialogTitle className="sr-only">Fullscreen Image View</DialogTitle>
          <button
            onClick={() => setFullscreenImage(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          {fullscreenImage && (
            <img
              src={getAssetUrl(fullscreenImage)}
              alt="Fullscreen pose"
              className="max-w-full max-h-full object-contain"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Video Playback Modal */}
      <Dialog open={!!playingVideo} onOpenChange={() => setPlayingVideo(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl p-0">
          <DialogTitle className="sr-only">Video Playback</DialogTitle>
          <div className="aspect-video bg-black rounded-lg overflow-hidden">
            {playingVideo && (
              <video
                src={getAssetUrl(playingVideo)}
                className="w-full h-full object-contain"
                controls
                autoPlay
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Segment Editor Modal */}
      <Dialog open={showSegmentEditor} onOpenChange={setShowSegmentEditor}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-[95vw] w-[95vw] h-[90vh] max-h-[90vh] p-0 flex flex-col">
          <DialogTitle className="sr-only">Segment Editor</DialogTitle>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800 shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Sliders className="w-5 h-5 text-orange-400" />
                Segment Editor
              </h2>
              <Badge variant="outline" className="text-xs text-orange-400 border-orange-500/50">
                {existingState?.video_segments?.length || 0} segments
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSegmentEditor(false)}
              className="h-8 w-8 p-0 text-zinc-400 hover:text-white"
              data-testid="close-segment-editor-btn"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Segment Editor Content */}
          <div className="flex-1 overflow-hidden p-4">
            {existingState?.video_segments?.length > 0 && (
              <SegmentEditor
                initialSegments={existingState.video_segments.map((seg, idx) => ({
                  id: seg.segment_id !== undefined ? `segment_${seg.segment_id}` : `segment_${idx}`,
                  order: idx,
                  duration_ms: 5000,
                  trim_start_ms: seg.trim_start_ms || 0,
                  trim_end_ms: seg.trim_end_ms || 0,
                  speed: seg.speed || 1,
                  video_url: seg.video_url
                }))}
                speedMultiplier={speedMultiplier}
                onSpeedChange={setSpeedMultiplier}
                onCompile={handleCompileWithEdits}
                compiling={compiling}
                getAssetUrl={getAssetUrl}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ExtremeActionPanel;
